import { respectExclusions } from "./respectExclusions.ts";
import { player } from "../index.ts";
import { minorsProspects, organizationPlayers } from "./organizationPlayers.ts";
import { PLAYER } from "../../../common/constants.ts";

// Trade search assets: the big club, the organization's prospects (minorsProspects) and its three least valuable depth
// minor leaguers, which only close deals that are almost there (fillerFloors in packageValue.ts). Full farm systems are
// ~45 players, and trying each of them at every step of the search made AI roster management take minutes.
const MAX_FILLERS = 3;
const tradeAssetPlayers = async (tid: number) => {
	const players = await organizationPlayers(tid);
	const keep = minorsProspects(players);
	const depth = players.filter((p) => p.tid === PLAYER.MINORS && !keep.has(p.pid));
	depth.sort((a, b) => a.value - b.value || a.pid - b.pid);
	for (const p of depth.slice(0, MAX_FILLERS)) keep.add(p.pid);
	return players.filter((p) => p.tid !== PLAYER.MINORS || keep.has(p.pid));
};
import { idb } from "../../db/index.ts";
import type { DraftPick, Player, TradeTeams } from "../../../common/types.ts";
import isUntradable from "./isUntradable.ts";
import { helpers } from "../../util/index.ts";
import { COMPOSITE_WEIGHTS, POSITIONS } from "../../../common/constants.ts";
import { isSport } from "../../../common/sportFunctions.ts";
import { last } from "../../../common/utils.ts";
import type { ValueChangeCalculator } from "../team/ValueChangeCalculator.ts";

export type LookingFor = {
	positions: Set<string>;
	skills: Set<string>;
	draftPicks: boolean;
	prospects: boolean;
	bestCurrentPlayers: boolean;
};

type AssetBase = {
	tid: number;
	dv: number;

	// score is only used when applying some sort override that is not dv, with some lookingFor settings
	score: number;
};
type AssetPlayer = AssetBase & {
	type: "player";
	p: Player;
};
type AssetPick = AssetBase & {
	type: "draftPick";
	dp: DraftPick;
};
type Asset = AssetPlayer | AssetPick;

// Baseball: with "What are you looking for" set, everything the AI team gives must match it, not only the first
// player (filler picks and extra players included). Prospects = 24 or younger, best current players = on a big
// league roster, picks only when draft picks are asked for.
const PROSPECT_MAX_AGE = 24;
export const matchesLookingFor = (
	asset: { type: "player"; p: Player } | { type: "draftPick" },
	lookingFor: LookingFor,
) => {
	const wantsPlayers =
		lookingFor.positions.size > 0 ||
		lookingFor.skills.size > 0 ||
		lookingFor.prospects ||
		lookingFor.bestCurrentPlayers;
	if (asset.type === "draftPick") {
		return lookingFor.draftPicks;
	}
	if (!wantsPlayers) {
		return !lookingFor.draftPicks;
	}
	const r = last(asset.p.ratings);
	const labels = [...lookingFor.skills].map(
		(skill) => COMPOSITE_WEIGHTS[skill]?.skill?.label,
	);
	return (
		(lookingFor.positions.size === 0 || lookingFor.positions.has(r.pos)) &&
		(lookingFor.skills.size === 0 ||
			labels.some((label) => label !== undefined && r.skills.includes(label))) &&
		(!lookingFor.prospects ||
			r.season - asset.p.born.year <= PROSPECT_MAX_AGE) &&
		(!lookingFor.bestCurrentPlayers || asset.p.tid >= 0)
	);
};

const hasLookingFor = (lookingFor: LookingFor | undefined): lookingFor is LookingFor =>
	lookingFor !== undefined &&
	(lookingFor.positions.size > 0 ||
		lookingFor.skills.size > 0 ||
		lookingFor.draftPicks ||
		lookingFor.prospects ||
		lookingFor.bestCurrentPlayers);

// Add either the highest value asset or the lowest value one that makes the trade good for the AI team.
const tryAddAsset = async (
	teams: TradeTeams,
	holdUserConstant: boolean,
	valueChangeCalculator: ValueChangeCalculator,
	prevDv: number,
	firstTry: boolean,
	lookingFor?: LookingFor,
): Promise<TradeTeams | undefined> => {
	let assets: Asset[] = [];

	if (!holdUserConstant) {
		// Get all players not in userPids
		const players = await tradeAssetPlayers(teams[0].tid);

		for (const p of players) {
			if (
				teams[0].pids.includes(p.pid) ||
				teams[0].pidsExcluded.includes(p.pid) ||
				isUntradable(p).untradable
			) {
				continue;
			}

			assets.push({
				type: "player",
				dv: 0,
				score: 1,
				tid: teams[0].tid,
				p,
			});
		}
	}

	// Get all players not in otherPids
	const players = await tradeAssetPlayers(teams[1].tid);

	// If lookingFor is set, make sure first asset added is from one of the requested positions only
	let lookingForSpecificPositions;
	if (firstTry && lookingFor && lookingFor.positions.size > 0) {
		if (isSport("basketball")) {
			// For basketball, convert G/F/C into real positions
			lookingForSpecificPositions = new Set(
				POSITIONS.filter((pos) => {
					for (const pos2 of lookingFor.positions) {
						if (pos.includes(pos2)) {
							return true;
						}
					}
				}),
			);
		} else {
			// Other sports, pass right through
			lookingForSpecificPositions = lookingFor.positions;
		}
	}

	for (const p of players) {
		if (
			teams[1].pids.includes(p.pid) ||
			teams[1].pidsExcluded.includes(p.pid) ||
			isUntradable(p).untradable
		) {
			continue;
		}

		if (
			lookingForSpecificPositions &&
			!lookingForSpecificPositions.has(last(p.ratings).pos)
		) {
			continue;
		}

		assets.push({
			type: "player",
			dv: 0,
			score: 1,
			tid: teams[1].tid,
			p,
		});
	}

	if (!lookingForSpecificPositions) {
		if (!holdUserConstant) {
			// Get all draft picks not in userDpids
			const draftPicks = await idb.cache.draftPicks.indexGetAll(
				"draftPicksByTid",
				teams[0].tid,
			);

			for (const dp of draftPicks) {
				if (
					teams[0].dpids.includes(dp.dpid) ||
					teams[0].dpidsExcluded.includes(dp.dpid)
				) {
					continue;
				}

				assets.push({
					type: "draftPick",
					dv: 0,
					score: 0,
					tid: teams[0].tid,
					dp,
				});
			}
		}

		// Get all draft picks not in otherDpids
		const draftPicks = await idb.cache.draftPicks.indexGetAll(
			"draftPicksByTid",
			teams[1].tid,
		);

		for (const dp of draftPicks) {
			if (
				teams[1].dpids.includes(dp.dpid) ||
				teams[1].dpidsExcluded.includes(dp.dpid)
			) {
				continue;
			}

			assets.push({
				type: "draftPick",
				dv: 0,
				score: 0,
				tid: teams[1].tid,
				dp,
			});
		}
	}

	if (isSport("baseball") && hasLookingFor(lookingFor)) {
		assets = assets.filter(
			(asset) =>
				asset.tid !== teams[1].tid || matchesLookingFor(asset, lookingFor),
		);
	}

	// If there are no more to try, stop
	if (assets.length === 0) {
		return;
	}

	// Calculate the value for each asset added to the trade, for use in forward selection
	for (const asset of assets) {
		const userPids = teams[0].pids.slice();
		const otherPids = teams[1].pids.slice();
		const userDpids = teams[0].dpids.slice();
		const otherDpids = teams[1].dpids.slice();

		if (asset.type === "player") {
			if (asset.tid === teams[0].tid) {
				userPids.push(asset.p.pid);
			} else {
				otherPids.push(asset.p.pid);
			}
		} else {
			if (asset.tid === teams[0].tid) {
				userDpids.push(asset.dp.dpid);
			} else {
				otherDpids.push(asset.dp.dpid);
			}
		}

		asset.dv = await valueChangeCalculator.evaluate({
			tid: teams[1].tid,
			pidsAdd: userPids,
			pidsRemove: otherPids,
			dpidsAdd: userDpids,
			dpidsRemove: otherDpids,
			tradingPartnerTid: teams[0].tid,
		});
	}

	// Baseball: while the AI still wants more, it asks the user for more instead of "paying" with its own players that it
	// values below nothing (depth minor leaguers, bad contracts), which piled junk onto the user's side
	if (isSport("baseball") && prevDv <= 0) {
		assets = assets.filter((asset) => asset.tid === teams[0].tid);
	}

	if (prevDv > 0) {
		// Here we are trying to find a single asset for the AI to give up (or a negative asset from the user) while still having the trade be favorable to them. So we are discarding assets that are too good to give up. If there is no asset remaining (dv is negative for all assets) then just give up.
		// prevDv condition is basically just for the trading block with lookingFor - we want to make sure the assets we're picking actually move things in the correct direction, which is generally starting with a positive dv and moving down towards 0. Maybe would make sense to put a similar condition for negative dv, but idk if it's needed.
		assets = assets.filter((asset) => asset.dv > 0 && asset.dv < prevDv);
	} else {
		// Going in the opposite direction, make sure this asset actually moves us that way
		assets = assets.filter((asset) => asset.dv > prevDv);
	}

	if (assets.length === 0) {
		return;
	}

	// Sort the assets such that the best assets are listed first. Why best and not worst? Because we already got rid of assets that are so good they would make the trade negative value for the AI. So the AI wants to offer the best asset of its remaining assets, otherwise the trade will remain too unbalanced.
	let lookingForSort = false;
	if (lookingFor) {
		if (lookingFor.skills.size > 0) {
			for (const asset of assets) {
				if (asset.type === "player") {
					const ratings = last(asset.p.ratings);
					for (const skill of lookingFor.skills) {
						asset.score += player.compositeRating(
							ratings,
							COMPOSITE_WEIGHTS[skill]!.ratings,
							COMPOSITE_WEIGHTS[skill]!.weights,
							false,
						);
					}
				}
			}

			lookingForSort = true;
		}

		if (lookingFor.bestCurrentPlayers) {
			for (const asset of assets) {
				if (asset.type === "player") {
					// Asset score for players starts at 0 and draft picks starts at 0, so this will keep all players above draft picks, and also handle adjusting based on skills-adjusted score
					asset.score *= asset.p.valueNoPot;
				}
			}

			lookingForSort = true;
		} else if (lookingFor.prospects) {
			for (const asset of assets) {
				if (asset.type === "player") {
					const ratings = last(asset.p.ratings);
					const potDiff = ratings.pot - ratings.ovr;
					asset.score += potDiff / 20;
				}
			}

			lookingForSort = true;
		}
	}

	if (lookingForSort) {
		assets.sort((a, b) => b.score - a.score);
	} else {
		// This is the default code path, unless lookingFor something!
		if (prevDv > 0) {
			// If prevDv>0, then we're looking for the best asset that keeps dv above 0, which is just the lowest value of dv in this array because above we already filtered out the ones that move this trade negative.
			// High dv means the trade is highly favorable to the AI, so this is sorting from the best asset (lowest dv) to the worst asset (high dv). AI wants to give up the most possible (best asset) while still minimizing dv (so the trade is favorable to them, but at least semi-close to favorable for the other team too).
			assets.sort((a, b) => a.dv - b.dv);
		} else {
			// Otherwise, we're looking to go from negative dv to positive dv. This could be all in one shot (pick the lowest positive dv) or it could require multiple assets (pick the highest negative dv, and then next iteration maybe it can get to positive dv).
			const positiveDv = [];
			const negativeDv = [];
			for (const asset of assets) {
				if (asset.dv > 0) {
					positiveDv.push(asset);
				} else {
					negativeDv.push(asset);
				}
			}
			positiveDv.sort((a, b) => a.dv - b.dv);
			negativeDv.sort((a, b) => b.dv - a.dv);
			assets = [...positiveDv, ...negativeDv];
		}
	}

	let asset;

	if (lookingFor) {
		const lookingForSomePlayerBeforeDraftPicks =
			firstTry &&
			lookingFor.draftPicks &&
			(lookingFor.bestCurrentPlayers ||
				lookingFor.prospects ||
				lookingFor.skills.size > 0);
		if (
			!lookingForSpecificPositions &&
			!lookingForSomePlayerBeforeDraftPicks &&
			lookingFor.draftPicks
		) {
			// If we're looking for draft picks (and we're done looking for 1 player for a specific position or 1 player with some other sort, if necessary), add draft picks first before players. If there are no draft picks that the AI team is willing to give up, then asset will be undefined and it will try to find a player below.
			asset = assets.find((asset) => asset.type === "draftPick");
		}
	}

	// Find the asset that will push the trade value the smallest amount above 0 (i.e. the best asset the AI is willing to give up without making the trade unfavorable to them), or fall back to just adding the worst asset if no single asset is good enough
	if (!asset) {
		// asset is now guaranteed to be defined, since there was a length check above
		asset = assets[0]!;
	}

	const newTeams = helpers.deepCopy(teams);
	if (asset.type === "player") {
		if (asset.tid === newTeams[0].tid) {
			newTeams[0].pids.push(asset.p.pid);
		} else {
			newTeams[1].pids.push(asset.p.pid);
		}
	} else {
		if (asset.tid === newTeams[0].tid) {
			newTeams[0].dpids.push(asset.dp.dpid);
		} else {
			newTeams[1].dpids.push(asset.dp.dpid);
		}
	}

	return newTeams;
};

/**
 * Make a trade work
 *
 * Have the AI add players/picks until they like the deal. Uses forward selection to try to find the first deal the AI likes.
 *
 * @memberOf core.trade
 * @param {Array.<Object>} teams Array of objects containing the assets for the two teams in the trade. The first object is for the user's team and the second is for the other team. Values in the objects are tid (team ID), pids (player IDs) and dpids (draft pick IDs).
 * @param {boolean} holdUserConstant If true, then players/picks will only be added from the other team. This is useful for the trading block feature.
 * @param {?Object} estValuesCached Estimated draft pick values from trade.getPickValues, or null. Only pass if you're going to call this repeatedly, then it'll be faster if you cache the values up front.
 * @return {Promise.<?Object>} If it works, resolves to a teams object (similar to first input) with the "made it work" trade info. Otherwise, resolves to undefined
 */
const search = async (
	teams: TradeTeams,
	{
		holdUserConstant,
		lookingFor,
		maxAssetsToAdd = Infinity,
		valueChangeCalculator,
	}: {
		holdUserConstant: boolean;
		lookingFor?: LookingFor;
		maxAssetsToAdd?: number;
		valueChangeCalculator: ValueChangeCalculator;
	},
): Promise<TradeTeams | undefined> => {
	teams = respectExclusions(teams);
	let initialSign: -1 | 1;
	let added = 0;

	let prevDv = await valueChangeCalculator.evaluate({
		tid: teams[1].tid,
		pidsAdd: teams[0].pids,
		pidsRemove: teams[1].pids,
		dpidsAdd: teams[0].dpids,
		dpidsRemove: teams[1].dpids,
		tradingPartnerTid: teams[0].tid,
	});

	if (prevDv > 0) {
		// Try to make trade better for user's team
		initialSign = 1;
	} else {
		// Try to make trade better for AI team
		initialSign = -1;
	}

	let prevTeams: TradeTeams = teams;
	while (true) {
		// Add assets until this trade is just barely good enough for the AI
		const newTeams = await tryAddAsset(
			prevTeams,
			holdUserConstant,
			valueChangeCalculator,
			prevDv,
			added === 0,
			lookingFor,
		);

		if (!newTeams) {
			// No improvement to offer found

			const dv = await valueChangeCalculator.evaluate({
				tid: prevTeams[1].tid,
				pidsAdd: prevTeams[0].pids,
				pidsRemove: prevTeams[1].pids,
				dpidsAdd: prevTeams[0].dpids,
				dpidsRemove: prevTeams[1].dpids,
				tradingPartnerTid: prevTeams[0].tid,
			});

			if (dv > 0) {
				return prevTeams;
			}

			return;
		}

		added += 1;

		const dv = await valueChangeCalculator.evaluate({
			tid: newTeams[1].tid,
			pidsAdd: newTeams[0].pids,
			pidsRemove: newTeams[1].pids,
			dpidsAdd: newTeams[0].dpids,
			dpidsRemove: newTeams[1].dpids,
			tradingPartnerTid: newTeams[0].tid,
		});

		// If adding assets moves the trade in the wrong direction, stop
		const dvDiffSign = Math.sign(prevDv - dv);

		if (dvDiffSign !== initialSign) {
			if (prevDv > 0) {
				return prevTeams;
			}

			return;
		}

		if (initialSign === -1) {
			// Looking for a trade that the AI will accept
			if (dv > 0) {
				// Run another round of makeItWork in the opposite direction, which will make the end result of this stable (clicking the "What would make this deal work?" button won't do anything)
				const newMaxAssetsToAdd = maxAssetsToAdd - added;
				if (newMaxAssetsToAdd > 0) {
					return search(newTeams, {
						holdUserConstant,
						maxAssetsToAdd: newMaxAssetsToAdd,
						valueChangeCalculator,
					});
				}
				return newTeams;
			}
		} else {
			// Looking for the closest to dv=0 that the AI will accept
			if (dv < 0 && prevDv > 0) {
				return prevTeams;
			}
		}

		if (added >= maxAssetsToAdd) {
			if (dv > 0) {
				return newTeams;
			}

			return;
		}

		prevTeams = newTeams;
		prevDv = dv;
	}
};

// Local refinement uses exactly the acceptance utility. No raw-value shortcut.
// Deterministic: the result depends only on the set of assets, never on the order they were added, and assets the
// user put in the offer are never removed. Evaluations are memoized (the same package is often scored twice).
const makeItWork = async (teams:TradeTeams, options:Parameters<typeof search>[1]) => {
 const found=await search(teams,options);
 if(!found || !isSport("baseball") || options.holdUserConstant || options.lookingFor)return found;
 const locked={pids:new Set(teams[0].pids),dpids:new Set(teams[0].dpids)};
 const sorted=(xs:number[])=>[...xs].sort((a,b)=>a-b);
 const memo=new Map<string,number>();
 const score=async(t:TradeTeams)=>{
  const key=JSON.stringify([sorted(t[0].pids),sorted(t[0].dpids),sorted(t[1].pids),sorted(t[1].dpids)]);
  let value=memo.get(key);
  if(value===undefined){value=await options.valueChangeCalculator.evaluate({tid:t[1].tid,
   pidsAdd:t[0].pids,pidsRemove:t[1].pids,dpidsAdd:t[0].dpids,dpidsRemove:t[1].dpids,tradingPartnerTid:t[0].tid});memo.set(key,value);}
  return value;
 };
 let best:TradeTeams=found;
 let bestScore=await score(best);
 // Remove unnecessary incoming assets: always the removal leaving the smallest acceptable surplus (ties: lowest id).
 const prune=async()=>{
  while(true){
   let choice:{trial:TradeTeams;value:number}|undefined;
   for(const key of ['pids','dpids'] as const)for(const id of sorted(best[0][key])){
    if(locked[key].has(id))continue;
    const trial=helpers.deepCopy(best);trial[0][key]=trial[0][key].filter(x=>x!==id);
    const value=await score(trial);
    if(value>0 && value<=bestScore && (!choice || value<choice.value))choice={trial,value};
   }
   if(!choice)return;
   best=choice.trial;bestScore=choice.value;
  }
 };
 await prune();
 // Consolidation (2-3 weak assets for 1 better one) needs at least 2 assets the refinement is allowed to remove.
 const removableNow=sorted(best[0].pids).filter(pid=>!locked.pids.has(pid));
 if(removableNow.length>=2){
  const candidates=(await organizationPlayers(teams[0].tid)).filter(p=>!best[0].pids.includes(p.pid)&&!teams[0].pidsExcluded.includes(p.pid)&&!isUntradable(p).untradable)
   .sort((a,b)=>b.value-a.value||a.pid-b.pid).slice(0,16);
  const ranked=[];
  for(const p of candidates){const trial=helpers.deepCopy(best);trial[0].pids.push(p.pid);ranked.push({pid:p.pid,gain:(await score(trial))-bestScore});}
  ranked.sort((a,b)=>b.gain-a.gain||a.pid-b.pid);
  const removable=[];
  for(const pid of removableNow){const trial=helpers.deepCopy(best);trial[0].pids=trial[0].pids.filter(x=>x!==pid);removable.push({pid,cost:bestScore-await score(trial)});}
  removable.sort((a,b)=>a.cost-b.cost||a.pid-b.pid);
  const weak=removable.slice(0,6).map(x=>x.pid);
  for(let a=0;a<weak.length;a++)for(let b=a+1;b<weak.length;b++)for(const c of [-1,...Array.from({length:weak.length-b-1},(_,i)=>i+b+1)]){
   const remove=[weak[a]!,weak[b]!,...(c>=0?[weak[c]!]:[])];
   if(!remove.every(pid=>best![0].pids.includes(pid)))continue;
   for(const candidate of ranked.slice(0,12)){
    if(best[0].pids.includes(candidate.pid))continue;
    const trial=helpers.deepCopy(best);trial[0].pids=trial[0].pids.filter(pid=>!remove.includes(pid));trial[0].pids.push(candidate.pid);
    const value=await score(trial);
    if(value>0 && value<bestScore){best=trial;bestScore=value;}
   }
  }
  // Consolidation can make an earlier pick/player redundant; prune again.
  await prune();
 }
 return respectExclusions(best);
};
/**
 * Trading Block (baseball): the first package the AI accepts is just a starting point. Bounded local search over the
 * AI team's most valuable assets (swap / add / remove one at a time, most promising first) keeps any change that the
 * AI still accepts with its full evaluation and that is better for the user with the user's full evaluation (fit,
 * roster impact, contracts). Fixed evaluation budget per team; not exhaustive, so offers can still be imperfect.
 */
export const improveBlockOffer = async (
	offer: TradeTeams,
	{
		maxAssetsToAdd,
		valueChangeCalculator,
		budget = 80,
	}: { maxAssetsToAdd: number; valueChangeCalculator: ValueChangeCalculator; budget?: number },
): Promise<TradeTeams> => {
	if (!isSport("baseball")) {
		return offer;
	}
	const [user, ai] = offer;
	let evaluations = 0;
	const memo = new Map<string, number>();
	const evaluate = async (tid: number, partner: number, add: [number[], number[]], remove: [number[], number[]]) => {
		const key = JSON.stringify([tid, [...add[0]].sort((a, b) => a - b), [...add[1]].sort((a, b) => a - b), [...remove[0]].sort((a, b) => a - b), [...remove[1]].sort((a, b) => a - b)]);
		let value = memo.get(key);
		if (value === undefined) {
			evaluations += 1;
			value = await valueChangeCalculator.evaluate({ tid, pidsAdd: add[0], dpidsAdd: add[1], pidsRemove: remove[0], dpidsRemove: remove[1], tradingPartnerTid: partner });
			memo.set(key, value);
		}
		return value;
	};
	const aiValue = (pids: number[], dpids: number[]) => evaluate(ai.tid, user.tid, [user.pids, user.dpids], [pids, dpids]);
	const userValue = (pids: number[], dpids: number[]) => evaluate(user.tid, ai.tid, [pids, dpids], [user.pids, user.dpids]);

	const players = (await organizationPlayers(ai.tid))
		.filter((p) => !ai.pidsExcluded.includes(p.pid) && !isUntradable(p).untradable)
		.sort((a, b) => b.value - a.value || a.pid - b.pid);
	const valueOf = new Map(players.map((p) => [p.pid, p.value]));
	const picks = (await idb.cache.draftPicks.indexGetAll("draftPicksByTid", ai.tid))
		.filter((dp) => dp.round === 1 && !ai.dpidsExcluded.includes(dp.dpid))
		.sort((a, b) => Number(a.season) - Number(b.season) || a.dpid - b.dpid)
		.slice(0, 2);
	const poolPids = players.slice(0, 8).map((p) => p.pid);
	const poolDpids = picks.map((dp) => dp.dpid);

	let current = { pids: [...ai.pids], dpids: [...ai.dpids] };
	let currentUser = await userValue(current.pids, current.dpids);
	// Rough ordering only (player value; picks rank like a decent player) so the budget goes to promising moves first
	const rough = (type: "p" | "d", id: number) => (type === "p" ? (valueOf.get(id) ?? 0) : 60);
	while (evaluations < budget) {
		const inSet: ["p" | "d", number][] = [...current.pids.map((id) => ["p", id] as ["p", number]), ...current.dpids.map((id) => ["d", id] as ["d", number])];
		const outSet: ["p" | "d", number][] = [
			...poolPids.filter((id) => !current.pids.includes(id)).map((id) => ["p", id] as ["p", number]),
			...poolDpids.filter((id) => !current.dpids.includes(id)).map((id) => ["d", id] as ["d", number]),
		];
		const moves: { pids: number[]; dpids: number[]; gain: number }[] = [];
		const apply = (remove?: ["p" | "d", number], add?: ["p" | "d", number]) => {
			const pids = current.pids.filter((id) => !(remove?.[0] === "p" && remove[1] === id));
			const dpids = current.dpids.filter((id) => !(remove?.[0] === "d" && remove[1] === id));
			if (add) (add[0] === "p" ? pids : dpids).push(add[1]);
			return { pids, dpids };
		};
		for (const a of inSet) for (const b of outSet) moves.push({ ...apply(a, b), gain: rough(b[0], b[1]) - rough(a[0], a[1]) });
		if (inSet.length < maxAssetsToAdd) for (const b of outSet) moves.push({ ...apply(undefined, b), gain: rough(b[0], b[1]) });
		if (inSet.length > 1) for (const a of inSet) moves.push({ ...apply(a), gain: -rough(a[0], a[1]) });
		moves.sort((x, y) => y.gain - x.gain || JSON.stringify(x).localeCompare(JSON.stringify(y)));

		let improved = false;
		for (const move of moves) {
			if (evaluations >= budget) break;
			if (move.pids.length === 0 && move.dpids.length === 0) continue;
			if ((await aiValue(move.pids, move.dpids)) <= 0) continue;
			const value = await userValue(move.pids, move.dpids);
			if (value > currentUser + 1e-9) {
				current = { pids: move.pids, dpids: move.dpids };
				currentUser = value;
				improved = true;
				break;
			}
		}
		if (!improved) break;
	}
	return [user, { ...ai, pids: current.pids, dpids: current.dpids }];
};

export default makeItWork;
